/**
 * Runtime settings — values that apply platform-wide, on the Settings page
 * (a tab of Tasks from Feature 70 until optum-agent Feature 87 made it a
 * destination again).
 *
 * Each setting names its deploy-time fallback so "empty" always has a
 * visible meaning. Settings saved here apply within a second (the agent
 * reads them per dispatcher tick); nothing on this page needs a restart.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import {
  getTaskConcurrencySetting,
  listSettingFields,
  revealSettingField,
  setTaskConcurrencySetting,
  updateSettingField,
} from '@optum/api-client/endpoints/platform-settings';
import type { SettingField as SettingFieldModel } from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { Band } from '../../ui/Band.js';
import { Field } from '../../ui/Field.js';
import { Badge, Input } from '../../ui/finixt.js';
import { PriorityDefault } from './PriorityDefault.js';

export function RuntimeSettings() {
  return (
    <div>
      <Band label="Task concurrency" style={{ marginBottom: 11 }} />
      <div
        style={{
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          padding: 16,
        }}
      >
        <TaskConcurrencyDefault />
      </div>

      <Band label="Capability priority" style={{ marginTop: 30, marginBottom: 11 }} />
      <div
        style={{
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          padding: 16,
        }}
      >
        <PriorityDefault />
      </div>

      <RuntimeSettingSections />
    </div>
  );
}

const SECTION_TITLES: Record<string, string> = {
  vector_store: 'Vector store',
  object_storage: 'Object storage',
};

// Ingestion is an infra plugin since Feature 84 (D19): its settings moved
// into its two capabilities' config, edited on the plugin's page.
const INGESTION_PLUGIN = 'optum-capability-ingestion';
const INGESTION_CAPABILITIES = ['document_ingestion', 'record_ingestion'];

/** Where ingestion is configured now, in the place its defaults used to be. */
function IngestionPointer() {
  return (
    <div>
      <Band label="Ingestion" style={{ marginTop: 30, marginBottom: 11 }} />
      <div
        style={{
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          padding: 16,
          fontSize: 'var(--text-body-md)',
          color: 'var(--text-muted)',
        }}
      >
        Ingestion is configured on its capabilities:{' '}
        {INGESTION_CAPABILITIES.map((id, index) => (
          <Fragment key={id}>
            {index > 0 && ' · '}
            <Link
              to={`/plugins/capabilities/${INGESTION_PLUGIN}?capability=${id}`}
              style={{ color: 'var(--text-accent)' }}
              className="mono"
            >
              {id}
            </Link>
          </Fragment>
        ))}
        . An organization sets its own on its workspace's Capabilities page.
      </div>
    </div>
  );
}

function RuntimeSettingSections() {
  const fields = useQuery({
    queryKey: qk.settingFields(),
    queryFn: listSettingFields,
  });

  if (fields.isPending) return <LoadingBlock />;
  if (fields.isError) {
    return (
      <ErrorBlock
        message={fields.error instanceof Error ? fields.error.message : 'Failed to load'}
      />
    );
  }

  const sections = new Map<string, SettingFieldModel[]>();
  const entries = Array.isArray(fields.data) ? fields.data : [];
  for (const field of entries) {
    const list = sections.get(field.section) ?? [];
    list.push(field);
    sections.set(field.section, list);
  }

  return (
    <>
      {[...sections.entries()].map(([section, entries]) => (
        <Fragment key={section}>
          <div>
            <Band
              label={SECTION_TITLES[section] ?? section}
              meta={entries.some((e) => e.applies === 'restart') ? 'applies on restart' : undefined}
              style={{ marginTop: 30, marginBottom: 11 }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 11,
                borderRadius: 'var(--radius-card)',
                border: '1px solid var(--border-hairline)',
                background: 'var(--surface-card)',
                padding: 16,
              }}
            >
              {entries.map((entry) =>
                entry.secret ? (
                  <SecretSettingRow key={entry.key} entry={entry} />
                ) : (
                  <PlainSettingRow key={entry.key} entry={entry} />
                ),
              )}
            </div>
          </div>
          {section === 'vector_store' && <IngestionPointer />}
        </Fragment>
      ))}
    </>
  );
}

function PlainSettingRow({ entry }: { entry: SettingFieldModel }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(entry.value == null ? '' : String(entry.value));

  const save = useMutation({
    mutationFn: () => updateSettingField(entry.key, value.trim() === '' ? null : value.trim()),
    onSuccess: (next) => {
      toast.success(`Saved ${entry.key}`);
      qc.setQueryData(qk.settingFields(), next);
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <Field id={`ps-${entry.key}`} label={entry.key} hint={settingHint(entry)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Input
          id={`ps-${entry.key}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={entry.env_fallback_set ? 'deployed value' : 'not set'}
        />
        <Button variant="secondary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Field>
  );
}

function SecretSettingRow({ entry }: { entry: SettingFieldModel }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);

  const [guardedUpdate, updateDialog] = useStepUp(updateSettingField);
  const [guardedReveal, revealDialog] = useStepUp(revealSettingField);

  const save = useMutation({
    mutationFn: () => guardedUpdate(entry.key, value.trim() === '' ? null : value),
    onSuccess: (next) => {
      toast.success(`Saved ${entry.key}`);
      setEditing(false);
      setValue('');
      qc.setQueryData(qk.settingFields(), next);
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  const reveal = useMutation({
    mutationFn: () => guardedReveal(entry.key),
    onSuccess: (out) => {
      setRevealed(out.value == null ? '(nothing stored)' : String(out.value));
    },
    onError: (e) => toast.error('Reveal failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {updateDialog}
      {revealDialog}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
          {entry.key}
        </span>
        <Badge tone={entry.is_set ? 'ok' : 'warn'}>{entry.is_set ? 'set' : 'not set'}</Badge>
        {entry.applies === 'restart' && <Badge tone="warn">restart</Badge>}
        <span style={{ flexGrow: 1 }} />
        {entry.is_set && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => (revealed == null ? reveal.mutate() : setRevealed(null))}
            disabled={reveal.isPending}
          >
            {revealed == null ? 'Reveal' : 'Hide'}
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Cancel' : 'Set value'}
        </Button>
      </div>
      <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        {settingHint(entry)}
      </p>
      {revealed != null && (
        <p
          style={{
            wordBreak: 'break-all',
            borderRadius: 'var(--radius-chip)',
            background: 'var(--surface-sunken)',
            padding: 7,
            fontSize: 'var(--text-meta-lg)',
          }}
          className="mono"
        >
          {revealed}
        </p>
      )}
      {editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="new value (empty clears)"
            autoComplete="off"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

function settingHint(entry: SettingFieldModel): string {
  const fallback = entry.env_fallback_set
    ? 'Empty uses the deployed environment value.'
    : 'No deployed fallback; empty means unset.';
  const applies =
    entry.applies === 'restart' ? ' Applies on the next restart.' : ' Applies within a second.';
  return `${entry.description} ${fallback}${applies}`;
}

function TaskConcurrencyDefault() {
  const [globalValue, setGlobalValue] = useState('');
  const [orgValue, setOrgValue] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const setting = useQuery({
    queryKey: qk.taskConcurrencySetting(),
    queryFn: getTaskConcurrencySetting,
  });

  useEffect(() => {
    if (!setting.data) return;
    setGlobalValue(
      setting.data.global_max_concurrent_tasks == null
        ? ''
        : String(setting.data.global_max_concurrent_tasks),
    );
    setOrgValue(
      setting.data.org_default_max_concurrent_tasks == null
        ? ''
        : String(setting.data.org_default_max_concurrent_tasks),
    );
  }, [setting.data]);

  const save = useMutation({
    mutationFn: () =>
      setTaskConcurrencySetting({
        global_max_concurrent_tasks: parseLimit(globalValue),
        org_default_max_concurrent_tasks: parseLimit(orgValue),
      }),
    onSuccess: (next) => {
      toast.success('Saved');
      qc.setQueryData(qk.taskConcurrencySetting(), next);
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (setting.isPending) return <LoadingBlock />;
  if (setting.isError) {
    return (
      <ErrorBlock
        message={setting.error instanceof Error ? setting.error.message : 'Failed to load settings'}
      />
    );
  }

  function onSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        Two limits, outermost first. The platform limit is how many tasks this platform runs at once
        across every organization. Beneath it, an organization runs at most its own limit — the
        default here, or the one set on its detail page — and its claims rotate across its plugins
        so no single plugin holds the organization's slots.
      </p>
      <Field
        id="set-global"
        label="Max concurrent tasks (platform-wide)"
        hint={`Across all organizations. Empty uses the deployed value (${setting.data.env_global}).`}
      >
        <Input
          id="set-global"
          type="number"
          min={1}
          value={globalValue}
          onChange={(e) => setGlobalValue(e.target.value)}
          placeholder={String(setting.data.env_global)}
        />
      </Field>
      <Field
        id="set-mct"
        label="Default limit per organization"
        hint={`What one organization may run when it has no limit of its own. Empty uses the deployed value (${setting.data.env_org_default}).`}
      >
        <Input
          id="set-mct"
          type="number"
          min={1}
          value={orgValue}
          onChange={(e) => setOrgValue(e.target.value)}
          placeholder={String(setting.data.env_org_default)}
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function parseLimit(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Math.max(1, Number(trimmed) || 1);
}
